import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Card, CardBody, CardHeader, Button, ButtonGroup,
  Row, Col, Container, Spinner, Form, FormGroup, Label, Input,
  Table,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem
} from 'reactstrap';
import { format, parseISO, formatISO, parse } from 'date-fns';
import { fr } from 'date-fns/locale';
import { saleActions } from '../../sagas/saleSlice';
import { FaCalendarAlt, FaColumns, FaDownload, FaUser, FaFileAlt, FaPrint, FaSearch } from 'react-icons/fa';
import { convertNumberToWords, formatPrice } from '../../helpers/utility';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import Select from "react-select";
import autoTable from 'jspdf-autotable';
import { supplierActions } from '../../sagas/supplierSlice';
import { purchaseActions } from '../../sagas/purchaseSlice';
import { productActions } from '../../sagas/productSlice';
import { transporteurActions } from '../../sagas/transporteurSlice';
import { entrepotActions } from '../../sagas/entrepotSlice';

const AchatsStatistiques = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { company } = useSelector(state => state.auth);
  const [brFilter, setBrFilter] = useState('');
  const [designationFilter, setDesignationFilter] = useState('');
  const unites = useSelector(status => status.products.unites);
  const { transporteurs } = useSelector(state => state.transporteurs);
  const { entrepots } = useSelector(state => state.entrepots);

  const now = new Date();
  const todayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrowDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  const pad = (n) => n.toString().padStart(2, '0');
  const formatDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

  useEffect(() => {
    dispatch(productActions.findAllUnites());
    dispatch(entrepotActions.list());
    dispatch(purchaseActions.clearSupplierReport());
  }, []);

  const parseDate = (dateString) => {
    if (!dateString) return new Date();
    if (dateString.includes('-')) {
      return parseISO(dateString);
    }
    return parse(dateString, 'dd/MM/yyyy', new Date());
  };

  const formatNumber = (value) => {
    const number = Number(value || 0);
    return Number.isInteger(number) ? number.toString() : number.toFixed(2);
  };

  const getPoutrelleNueLength = (designation) => {
    const match = String(designation || "")
      .trim()
      .match(/^poutrelle\s+nue\s+(\d+(?:[.,]\d+)?)\s*\/\s*\d+\b/i);

    return match ? Number(match[1].replace(",", ".")) : null;
  };

  // State
  const [dateRange, setDateRange] = useState('month');
  const [customDateRange, setCustomDateRange] = useState({
    startDate: format(new Date(), 'yyyy-MM-dd'),
    endDate: format(new Date(Date.now() + 24 * 60 * 60 * 1000), 'yyyy-MM-dd')
  });

  // Supplier report state
  const { suppliers, loading: suppliersLoading } = useSelector(state => state.suppliers);
  const { supplierReport, supplierReportLoading, supplierReportError } = useSelector(state => state.purchases);
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const supplierReportRef = useRef(null);
  const [filters, setFilters] = useState({
    supplierId: null,
    transporteurId: null,
    entrepotId: null,
    dateDebut: formatDate(todayDate),
    dateFin: formatDate(tomorrowDate),
    query: '',
    brNumber: '',
    articleDesignation: '',
    utilisateur: null,
    packDalla: false
  });
  const [visibleColumns, setVisibleColumns] = useState({
    brNumber: true,
    blFNumber: true,
    date: true,
    createdBy: true,
    supplier: true,
    entrepot: true,
    barcode: true,
    designation: true,
    quantity: true,
    transporteur: true,
    unit: true,
    unitPrice: true,
    lineTotal: true
  });

  const fournisseurOptions = suppliers?.map(sup => ({
    solde: sup.solde,
    totalDebit: sup.totalDebit,
    totalCredit: sup.totalCredit,
    value: sup.id,
    label: sup.typeFournisseur === 'PARTICULIER' ? `${sup.nom} ${sup.prenom}` : sup.entreprise,
  })) ?? [];

  const fournisseurOptionsWithAll = [
    { value: null, label: t('common.all', 'Tous') },
    ...fournisseurOptions
  ];

  const getSupplierLabel = (note) => note?.supplier ? `${note.supplier.name} (${note.supplier.code})` : "Passager";
  const getTransporteurLabel = (note) => note?.transporteur ? `${note.transporteur.libelle} (${note.transporteur.matricule})` : 'N/A';
  const getMlValue = (note) => {
    const length = getPoutrelleNueLength(note?.designation);
    return length ? formatNumber(length * Number(note?.quantity || 0)) : "";
  };

  const reportColumns = [
    { key: 'brNumber', label: 'N° BR', className: 'text-center fw-semibold', pdfWidth: 18, value: note => note.brNumber || '-' },
    { key: 'blFNumber', label: 'N° BL F', className: 'text-center fw-semibold', pdfWidth: 20, value: note => note.blFNumber || '-' },
    { key: 'date', label: 'Date', className: 'text-center', pdfWidth: 18, value: note => format(parseDate(note.date), 'dd/MM/yyyy', { locale: fr }) },
    { key: 'createdBy', label: 'Utilisateur', className: 'text-center', pdfWidth: 20, value: note => note.createdBy || '-' },
    { key: 'supplier', label: 'Fournisseur', className: 'text-center', pdfWidth: 30, value: getSupplierLabel },
    { key: 'entrepot', label: 'Entrepot', className: 'text-center', pdfWidth: 22, value: note => note.entrepot || 'N/A' },
    { key: 'barcode', label: 'Code barre', className: 'text-center', pdfWidth: 24, value: note => note.barcode || note.codeBarre || '-' },
    { key: 'designation', label: 'Désignation', className: 'ps-2', pdfWidth: 48, value: note => note.designation || '-' },
    { key: 'quantity', label: 'Quantité', className: 'text-center', pdfWidth: 15, value: note => formatNumber(note.quantity) },
    { key: 'ml', label: 'ML', className: 'text-center', pdfWidth: 15, value: getMlValue, computed: true },
    { key: 'transporteur', label: 'Transporteur', className: 'text-center', pdfWidth: 24, value: getTransporteurLabel },
    { key: 'unit', label: 'UM', className: 'text-center', pdfWidth: 12, value: note => note.unit || '-' },
    { key: 'unitPrice', label: 'PU', className: 'text-end', pdfWidth: 16, price: true, value: note => formatPrice(note.unitPrice, false) },
    { key: 'lineTotal', label: 'Total', className: 'text-end fw-bold', pdfWidth: 18, price: true, value: note => formatPrice(note.lineTotal, false) }
  ];

  const detailTableColumns = reportColumns.filter(column => !column.computed && visibleColumns[column.key]);
  const getPdfColumns = (withPrices) => reportColumns.filter(column =>
    (column.computed || visibleColumns[column.key]) && (withPrices || !column.price)
  );
  const utilisateurOptions = Array.from(new Set([
    filters.utilisateur,
    ...(supplierReport?.bonsReception || []).map(note => note.createdBy)
  ].filter(Boolean))).map(value => ({ value, label: value }));

  const toggleColumn = (columnKey) => {
    setVisibleColumns(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  };

  // Effect for fetching data
  useEffect(() => {
    if (suppliers.length === 0) {
      // Fetch suppliers for supplier report
      const fetchSuppliers = async () => {
        dispatch(supplierActions.search({
          query: ""
        }));
      };

      fetchSuppliers();
    }
    dispatch(transporteurActions.list());

  }, []);

  useEffect(() => {
    generateSupplierReport();
  }, [filters.supplierId, filters.transporteurId, filters.entrepotId, filters.dateDebut, filters.dateFin, filters.brNumber, filters.articleDesignation, filters.utilisateur, filters.packDalla]);

  // Generate supplier report
  const generateSupplierReport = () => {
    dispatch(purchaseActions.generateSupplierReport({ ...filters }));
  };

  const exportSupplierReportToPDF = (withPrices = true) => {
    if (!supplierReport) return;

    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4"
    });

    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 8;
    const footerHeight = 16;
    const tableWidth = pageWidth - margin * 2;

    // ---- GREEN & WHITE THEME ----
    const primaryGreen = { r: 46, g: 204, b: 113 };
    const altRow = { r: 232, g: 248, b: 245 };
    const white = { r: 255, g: 255, b: 255 };
    const black = { r: 0, g: 0, b: 0 };
    const negativeRed = { r: 231, g: 76, b: 60 };

    // ---------------- HEADER ----------------
    const addPageHeader = () => {
      doc.setFillColor(primaryGreen.r, primaryGreen.g, primaryGreen.b);
      doc.rect(0, 0, pageWidth, 30, "F");

      doc.setFillColor(primaryGreen.r, primaryGreen.g, primaryGreen.b);
      doc.rect(0, 25, pageWidth, 5, "F");

      doc.setTextColor(white.r, white.g, white.b);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(22);
      doc.text("RAPPORT ACHAT FOURNISSEUR", pageWidth / 2, 15, { align: "center" });

      const formattedFrom = format(parseDate(supplierReport.period.from), "dd/MM/yyyy", { locale: fr });
      const formattedTo = format(parseDate(supplierReport.period.to), "dd/MM/yyyy", { locale: fr });

      doc.setFontSize(11);
      doc.setFont("helvetica", "normal");
      doc.text(`Période: du ${formattedFrom} au ${formattedTo}`, pageWidth / 2, 22, { align: "center" });
    };

    // ---------------- FOOTER ----------------
    const addPageFooter = () => {
      const y = pageHeight - footerHeight;

      doc.setDrawColor(200, 200, 200);
      doc.line(margin, y, pageWidth - margin, y);

      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);

      doc.text(`Document généré le: ${supplierReport.generationDate}`, pageWidth - margin, y + 5, { align: "right" });
      doc.text(company.name, margin, y + 5);
      doc.text(`Page ${doc.internal.getNumberOfPages()}`, pageWidth / 2, y + 5, { align: "center" });
    };

    // ---- FIRST PAGE HEADER ----
    addPageHeader();

    // ---- COMPANY INFO ----
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(black.r, black.g, black.b);
    doc.text(company.name, margin, 40);

    doc.setDrawColor(primaryGreen.r, primaryGreen.g, primaryGreen.b);
    doc.line(margin, 42, margin + 60, 42);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(`Adresse: ${company.address}`, margin, 47);
    doc.text(`Tél: ${company.phone1} | Email: ${company.email}`, margin, 52);
    doc.text(`RC: ${company.rc} | IF: ${company.identificationFiscale} | ICE: ${company.ice}`, margin, 57);

    if (supplierReport.supplier) {
      // ---- SUPPLIER BOX ----
      doc.setFillColor(white.r, white.g, white.b);
      doc.setDrawColor(primaryGreen.r, primaryGreen.g, primaryGreen.b);
      doc.roundedRect(pageWidth - margin - 80, 35, 80, 30, 3, 3, "FD");

      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(primaryGreen.r, primaryGreen.g, primaryGreen.b);
      doc.text("FOURNISSEUR", pageWidth - margin - 75, 42);

      doc.setDrawColor(primaryGreen.r, primaryGreen.g, primaryGreen.b);
      doc.line(pageWidth - margin - 75, 44, pageWidth - margin - 15, 44);

      doc.setTextColor(black.r, black.g, black.b);
      doc.setFont("helvetica", "normal");
      doc.text(`Nom: ${supplierReport.supplier.name}`, pageWidth - margin - 75, 50);
      doc.text(`Téléphone: ${supplierReport.supplier.phone}`, pageWidth - margin - 75, 55);
      doc.text(`Code: ${supplierReport.supplier.code}`, pageWidth - margin - 75, 60);
    }

    // ---------------- TABLE ----------------
    let headers = ["N° BR", "Date", "Entrepôt", "Désignation", "Qté", "UM", "PU", "Total"];

    let rows = supplierReport.bonsReception.map(br => [
      br.brNumber,
      format(parseDate(br.date), "dd/MM/yyyy", { locale: fr }),
      br.entrepot || "N/A",
      br.designation,
      br.quantity.toString(),
      br.unit,
      formatPrice(br.unitPrice, false),
      formatPrice(br.lineTotal, false)
    ]);

    let exportHeaders = withPrices ? headers : headers.slice(0, 6);
    let exportRows = withPrices ? rows : rows.map(row => row.slice(0, 6));
    let columnStyles = withPrices
      ? {
        0: { cellWidth: 24 },
        1: { cellWidth: 24 },
        2: { cellWidth: 34 },
        3: { cellWidth: 117 },
        4: { cellWidth: 18, halign: "center" },
        5: { cellWidth: 20, halign: "center" },
        6: { cellWidth: 22, halign: "right" },
        7: { cellWidth: 22, halign: "right" }
      }
      : {
        0: { cellWidth: 26 },
        1: { cellWidth: 28 },
        2: { cellWidth: 40 },
        3: { cellWidth: 153 },
        4: { cellWidth: 16, halign: "center" },
        5: { cellWidth: 18, halign: "center" }
      };

    const pdfColumns = getPdfColumns(withPrices);
    exportHeaders = pdfColumns.map(column => column.label);
    exportRows = supplierReport.bonsReception.map((note) =>
      pdfColumns.map(column => column.value(note))
    );
    const selectedWidth = pdfColumns.reduce((sum, column) => sum + column.pdfWidth, 0) || tableWidth;
    const widthRatio = tableWidth / selectedWidth;
    columnStyles = pdfColumns.reduce((styles, column, index) => {
      styles[index] = {
        cellWidth: Math.max(10, column.pdfWidth * widthRatio),
        halign: column.className?.includes('text-end') ? 'right' : column.className?.includes('text-center') ? 'center' : 'left'
      };
      return styles;
    }, {});
    autoTable(doc, {
      startY: 70,
      tableWidth,
      head: [exportHeaders],
      body: exportRows,
      theme: "grid",

      // ---- FIXED: green header with borders ----
      headStyles: {
        fillColor: [primaryGreen.r, primaryGreen.g, primaryGreen.b],
        textColor: [255, 255, 255],
        fontStyle: "bold",
        halign: "center",
        lineWidth: 0.3,
        lineColor: [0, 0, 0]
      },

      // ---- alternating rows ----
      alternateRowStyles: {
        fillColor: [altRow.r, altRow.g, altRow.b]
      },

      styles: {
        cellPadding: withPrices ? 1.5 : 1.9,
        fontSize: withPrices ? 8 : 8.3,
        textColor: [0, 0, 0],
        lineWidth: 0.15,
        lineColor: [210, 214, 220],
        overflow: "linebreak",
        valign: "middle"
      },

      columnStyles,
      didParseCell: (data) => {
        if (data.section === "head" && data.row.index === 0) {
          data.cell.styles.fillColor = [22, 101, 52];
          data.cell.styles.textColor = [255, 255, 255];
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.halign = "center";
        }
      },
      margin: { left: margin, right: margin, top: 35, bottom: footerHeight },
      didDrawPage: () => {
        addPageHeader();
        addPageFooter();
      }
    });

    const detailItems = supplierReport.bonsReception || [];
    const detailTotalQuantity = detailItems.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
    const detailTotalMl = detailItems.reduce((sum, item) => {
      const length = getPoutrelleNueLength(item.designation);
      return sum + (length ? length * Number(item.quantity || 0) : 0);
    }, 0);
    const detailTotalAmount = detailItems.reduce((sum, item) => sum + Number(item.lineTotal || 0), 0);
    const detailSummaryRows = [[
      detailItems.length.toString(),
      formatNumber(detailTotalQuantity),
      formatNumber(detailTotalMl),
      withPrices ? formatPrice(detailTotalAmount, false) : "-",
    ]];

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 8,
      head: [["Lignes", "Quantite totale", "Total ML", "Montant total"]],
      body: detailSummaryRows,
      theme: "grid",
      styles: { cellPadding: 2.2, fontSize: 9, textColor: [0, 0, 0], halign: "center" },
      headStyles: {
        fillColor: [primaryGreen.r, primaryGreen.g, primaryGreen.b],
        textColor: [255, 255, 255],
        fontStyle: "bold",
      },
      columnStyles: {
        0: { cellWidth: 60 },
        1: { cellWidth: 75 },
        2: { cellWidth: 70 },
        3: { cellWidth: 76 },
      },
      tableWidth,
      margin: { left: margin, right: margin, top: 35, bottom: footerHeight },
      didDrawPage: () => {
        addPageHeader();
        addPageFooter();
      },
    });

    if (false && withPrices && supplierReport.supplier) {
      // ---------------- SUMMARY ----------------
      let summaryY = doc.lastAutoTable.finalY + 12;

      // ---- FIXED: rounded green-bordered summary box ----
      const summaryW = 90;
      const summaryH = 40;

      if (summaryY + summaryH + 16 > pageHeight - footerHeight) {
        doc.addPage();
        addPageHeader();
        addPageFooter();
        summaryY = 40;
      }

      doc.setDrawColor(primaryGreen.r, primaryGreen.g, primaryGreen.b);
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(margin, summaryY, summaryW, summaryH, 3, 3, "FD");

      doc.setFont("helvetica", "bold");
      doc.setTextColor(primaryGreen.r, primaryGreen.g, primaryGreen.b);
      doc.setFontSize(12);
      doc.text("RÃ‰SUMÃ‰", margin + 5, summaryY + 7);

      const summary = supplierReport.summary;

      doc.setFont("helvetica", "normal");
      doc.setTextColor(black.r, black.g, black.b);
      doc.setFontSize(9);

      doc.text("Montant Total Consommé:", margin + 5, summaryY + 15);
      doc.text(formatPrice(summary.totalConsome, false), margin + summaryW - 5, summaryY + 15, { align: "right" });

      doc.text("Solde Avant :", margin + 5, summaryY + 22);
      doc.text(formatPrice(summary.soldeAvant, false), margin + summaryW - 5, summaryY + 22, { align: "right" });

      doc.text("Avance :", margin + 5, summaryY + 29);
      doc.text(formatPrice(summary.avance, false), margin + summaryW - 5, summaryY + 29, { align: "right" });

      doc.text("Avoir :", margin + 5, summaryY + 36);
      doc.text(formatPrice(summary.avoir, false), margin + summaryW - 5, summaryY + 36, { align: "right" });

      // ---- Net Ã  payer OUTSIDE the card ----
      const net = parseFloat(summary.netApaye);
      const netY = summaryY + summaryH + 10;

      doc.setFont("helvetica", "bold");
      doc.setTextColor(primaryGreen.r, primaryGreen.g, primaryGreen.b);
      doc.setFontSize(12);
      doc.text("Net Ã€ Payer :", margin, netY);

      doc.text(formatPrice(net, false), margin + 60, netY);
    }
    // ---------------- OPEN IN NEW TAB ----------------
    const blob = doc.output("blob");
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
  };

  const exportSupplierRecapToPDF = (withPrices = true) => {
    if (!supplierReport) return;

    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4"
    });

    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 8;
    const footerHeight = 16;
    const tableWidth = pageWidth - margin * 2;
    const primaryGreen = { r: 46, g: 204, b: 113 };
    const altRow = { r: 232, g: 248, b: 245 };
    const white = { r: 255, g: 255, b: 255 };
    const black = { r: 0, g: 0, b: 0 };
    const negativeRed = { r: 231, g: 76, b: 60 };

    const addPageHeader = () => {
      doc.setFillColor(primaryGreen.r, primaryGreen.g, primaryGreen.b);
      doc.rect(0, 0, pageWidth, 30, "F");
      doc.rect(0, 25, pageWidth, 5, "F");

      doc.setTextColor(white.r, white.g, white.b);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(22);
      doc.text("RECAP ACHAT FOURNISSEUR", pageWidth / 2, 15, { align: "center" });

      const formattedFrom = format(parseDate(supplierReport.period.from), "dd/MM/yyyy", { locale: fr });
      const formattedTo = format(parseDate(supplierReport.period.to), "dd/MM/yyyy", { locale: fr });

      doc.setFontSize(11);
      doc.setFont("helvetica", "normal");
      doc.text(`Période: du ${formattedFrom} au ${formattedTo}`, pageWidth / 2, 22, { align: "center" });
    };

    const addPageFooter = () => {
      const y = pageHeight - footerHeight;

      doc.setDrawColor(200, 200, 200);
      doc.line(margin, y, pageWidth - margin, y);
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      doc.text(`Document généré le: ${supplierReport.generationDate}`, pageWidth - margin, y + 5, { align: "right" });
      doc.text(company.name, margin, y + 5);
      doc.text(`Page ${doc.internal.getNumberOfPages()}`, pageWidth / 2, y + 5, { align: "center" });
    };

    addPageHeader();

    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(black.r, black.g, black.b);
    doc.text(company.name, margin, 40);

    doc.setDrawColor(primaryGreen.r, primaryGreen.g, primaryGreen.b);
    doc.line(margin, 42, margin + 60, 42);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(`Adresse: ${company.address}`, margin, 47);
    doc.text(`Tél: ${company.phone1} | Email: ${company.email}`, margin, 52);
    doc.text(`RC: ${company.rc} | IF: ${company.identificationFiscale} | ICE: ${company.ice}`, margin, 57);

    if (supplierReport.supplier) {
      doc.setFillColor(white.r, white.g, white.b);
      doc.setDrawColor(primaryGreen.r, primaryGreen.g, primaryGreen.b);
      doc.roundedRect(pageWidth - margin - 80, 35, 80, 30, 3, 3, "FD");

      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(primaryGreen.r, primaryGreen.g, primaryGreen.b);
      doc.text("FOURNISSEUR", pageWidth - margin - 75, 42);
      doc.line(pageWidth - margin - 75, 44, pageWidth - margin - 15, 44);

      doc.setTextColor(black.r, black.g, black.b);
      doc.setFont("helvetica", "normal");
      doc.text(`Nom: ${supplierReport.supplier.name}`, pageWidth - margin - 75, 50);
      doc.text(`Téléphone: ${supplierReport.supplier.phone}`, pageWidth - margin - 75, 55);
      doc.text(`Code: ${supplierReport.supplier.code}`, pageWidth - margin - 75, 60);
    }

    const recapMap = new Map();
    (supplierReport.bonsReception || []).forEach((br) => {
      const supplierName = br.supplier?.name || supplierReport?.supplier?.name || "N/A";
      const entrepotName = br.entrepot || "N/A";
      const designation = br.designation || "-";
      const unit = br.unit || "";
	      const unitPrice = Number(br.unitPrice || 0);
	      const quantity = Number(br.quantity || 0);
	      const lineTotal = Number(br.lineTotal || unitPrice * quantity || 0);
	      const poutrelleLength = getPoutrelleNueLength(designation);
	      const mlQuantity = poutrelleLength ? poutrelleLength * quantity : 0;
	      const key = withPrices
	        ? `${supplierName}__${designation}__${unit}__${unitPrice}`
        : `${supplierName}__${designation}__${unit}`;

      if (!recapMap.has(key)) {
        recapMap.set(key, {
          supplierName,
          designation,
          unit,
	          unitPrice,
	          quantity: 0,
	          lineTotal: 0,
	          mlQuantity: 0,
	          mlAmount: 0,
	          entrepots: new Set(),
	          brNumbers: new Set(),
        });
      }

	      const current = recapMap.get(key);
	      current.quantity += quantity;
	      current.lineTotal += lineTotal;
	      current.mlQuantity += mlQuantity;
	      current.mlAmount += mlQuantity > 0 ? lineTotal : 0;
	      current.entrepots.add(entrepotName);
      if (br.brNumber) current.brNumbers.add(br.brNumber);
    });

    const recapRows = Array.from(recapMap.values()).map((item) =>
      withPrices
        ? [
          item.supplierName,
          Array.from(item.entrepots).join(", "),
          Array.from(item.brNumbers).join(", "),
          item.designation,
          item.quantity.toString(),
          item.unit,
          formatPrice(item.unitPrice, false),
          formatPrice(item.lineTotal, false),
        ]
        : [
          item.supplierName,
          Array.from(item.entrepots).join(", "),
          Array.from(item.brNumbers).join(", "),
          item.designation,
          item.quantity.toString(),
          item.unit,
        ]
    );

    const headers = withPrices
      ? ["Fournisseur", "Entrepot(s)", "BR(s)", "Désignation", "Qté", "UM", "PU", "Total"]
      : ["Fournisseur", "Entrepot(s)", "BR(s)", "Désignation", "Qté", "UM"];

    let columnStyles = withPrices
      ? {
        0: { cellWidth: 36 },
        1: { cellWidth: 34 },
        2: { cellWidth: 42 },
        3: { cellWidth: 99 },
        4: { cellWidth: 16, halign: "center" },
        5: { cellWidth: 18, halign: "center" },
        6: { cellWidth: 18, halign: "right" },
        7: { cellWidth: 18, halign: "right" },
      }
      : {
        0: { cellWidth: 45 },
        1: { cellWidth: 38 },
        2: { cellWidth: 45 },
        3: { cellWidth: 119 },
        4: { cellWidth: 16, halign: "center" },
        5: { cellWidth: 18, halign: "center" },
      };

    autoTable(doc, {
      startY: 70,
      tableWidth,
      head: [headers],
      body: recapRows,
      theme: "grid",
      headStyles: {
        fillColor: [primaryGreen.r, primaryGreen.g, primaryGreen.b],
        textColor: [255, 255, 255],
        fontStyle: "bold",
        halign: "center",
      },
      alternateRowStyles: {
        fillColor: [altRow.r, altRow.g, altRow.b],
      },
      styles: {
        cellPadding: withPrices ? 1.5 : 1.9,
        fontSize: withPrices ? 8 : 8.3,
        textColor: [0, 0, 0],
        lineColor: [210, 214, 220],
        lineWidth: 0.15,
        overflow: "linebreak",
        valign: "middle",
      },
      columnStyles,
      didParseCell: (data) => {
        if (data.section === "head" && data.row.index === 0) {
          data.cell.styles.fillColor = [22, 101, 52];
          data.cell.styles.textColor = [255, 255, 255];
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.halign = "center";
        }
      },
      margin: { left: margin, right: margin, top: 35, bottom: footerHeight },
      didDrawPage: () => {
        addPageHeader();
        addPageFooter();
      },
    });

    const recapItems = Array.from(recapMap.values());
    const totalQuantity = recapItems.reduce((sum, item) => sum + item.quantity, 0);
    const totalAmount = recapItems.reduce((sum, item) => sum + item.lineTotal, 0);
    const totalMl = recapItems.reduce((sum, item) => sum + item.mlQuantity, 0);
    const totalMlAmount = recapItems.reduce((sum, item) => sum + item.mlAmount, 0);
    const recapSummaryRows = [[
      formatNumber(totalMl),
      withPrices ? formatPrice(totalMlAmount, false) : "-",
      formatNumber(totalQuantity),
      withPrices ? formatPrice(totalAmount, false) : "-",
    ]];

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 8,
      head: [["ML total", "Montant ML", "Quantite totale", "Montant total"]],
      body: recapSummaryRows,
      theme: "grid",
      styles: { cellPadding: 2.2, fontSize: 9, textColor: [0, 0, 0], overflow: "linebreak", halign: "center" },
      headStyles: {
        fillColor: [primaryGreen.r, primaryGreen.g, primaryGreen.b],
        textColor: [255, 255, 255],
        fontStyle: "bold",
      },
      columnStyles: {
        0: { cellWidth: 60 },
        1: { cellWidth: 75 },
        2: { cellWidth: 70 },
        3: { cellWidth: 76 },
      },
      tableWidth,
      margin: { left: margin, right: margin, top: 35, bottom: footerHeight },
      didDrawPage: () => {
        addPageHeader();
        addPageFooter();
      },
    });

    if (withPrices && supplierReport.summary) {
      const s = supplierReport.summary;
      const summaryHeight = 52;
      let y = doc.lastAutoTable.finalY + 12;

      if (y + summaryHeight + 14 > pageHeight - footerHeight) {
        doc.addPage();
        addPageHeader();
        addPageFooter();
        y = 40;
      }

      doc.setDrawColor(primaryGreen.r, primaryGreen.g, primaryGreen.b);
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(margin, y, 100, summaryHeight, 3, 3, "FD");

      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.setTextColor(primaryGreen.r, primaryGreen.g, primaryGreen.b);
      doc.text("RÉSUMÉ", margin + 5, y + 7);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(black.r, black.g, black.b);

      const summaryRows = [
        ["Montant Total Consommé :", s.totalConsome],
        ["Solde Avant :", s.soldeAvant],
        ["Avance :", s.avance],
        ["Règlements :", s.reglements],
        ["Avoir :", s.avoir],
      ];

      summaryRows.forEach(([label, value], index) => {
        const rowY = y + 15 + index * 7;
        doc.text(label, margin + 5, rowY);
        doc.text(formatPrice(value || 0, false), margin + 95, rowY, { align: "right" });
      });

      const net = Number(s.netApaye || 0);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(net < 0 ? negativeRed.r : primaryGreen.r, net < 0 ? negativeRed.g : primaryGreen.g, net < 0 ? negativeRed.b : primaryGreen.b);
      doc.text("Net à payer :", margin + 5, y + 48);
      doc.text(formatPrice(net, false), margin + 95, y + 48, { align: "right" });
    }

	    if (false) {
	      const recapItems = Array.from(recapMap.values());
	      const totalMl = recapItems.reduce((sum, item) => sum + item.mlQuantity, 0);
	      const totalMlAmount = recapItems.reduce((sum, item) => sum + item.mlAmount, 0);
	      const summaryHeight = withPrices ? 42 : 35;
      const summaryTopMargin = 15;
      const summaryBottomMargin = 10;
      let y = doc.lastAutoTable.finalY + summaryTopMargin;

      if (y + summaryHeight + summaryBottomMargin > pageHeight - footerHeight) {
        doc.addPage();
        addPageHeader();
        addPageFooter();
        y = 40;
      }

      doc.setDrawColor(primaryGreen.r, primaryGreen.g, primaryGreen.b);
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(margin, y, 90, summaryHeight, 3, 3, "FD");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.setTextColor(primaryGreen.r, primaryGreen.g, primaryGreen.b);
      doc.text("RÃ‰SUMÃ‰", margin + 5, y + 7);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(black.r, black.g, black.b);
      doc.text("Quantité totale :", margin + 5, y + 16);
	      doc.setFillColor(255, 255, 255);
	      doc.rect(margin + 4, y + 12, 82, 6, "F");
	      doc.setTextColor(black.r, black.g, black.b);
	      doc.text("Uniquement articles Poutrelle nue x.xx / yy", margin + 5, y + 16);
	      doc.text("Total ML :", margin + 5, y + 25);
	      doc.text(formatNumber(totalMl), margin + 85, y + 25, { align: "right" });
	      if (withPrices) {
	        doc.text("Montant total :", margin + 5, y + 34);
	        doc.text(formatPrice(totalMlAmount, false), margin + 85, y + 34, { align: "right" });
	      }
	      if (totalMl === 0) {
	        doc.setFontSize(7);
	        doc.text("Aucune ligne ML detectee dans ce recap.", margin + 5, y + (withPrices ? 40 : 32));
	      }
    }

    const blob = doc.output("blob");
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
  };

  const debouncedSearch = () => {
    dispatch(purchaseActions.generateSupplierReport({ ...filters }));
  }

  const transporteurOptions = transporteurs?.map(transporteur => ({
    value: transporteur.id,
    label: transporteur.code + ' - ' + transporteur.libelle,
  })) ?? [];

  const transporteurOptionsWithAll = [
    { value: null, label: t('common.all', 'Tous') },
    ...transporteurOptions
  ];

  const entrepotOptions = entrepots?.map(entrepot => ({
    value: entrepot.id,
    label: `${entrepot.code} - ${entrepot.libelle}`,
  })) ?? [];

  const entrepotOptionsWithAll = [
    { value: null, label: t('common.all', 'Tous') },
    ...entrepotOptions
  ];

  return (
    <Container fluid className="page-content">
      <Card className="shadow-sm mb-4">
        <CardHeader className="bg-white">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <FaFileAlt className="me-2 text-primary" />
              Détails des achats
            </h5>
            <div className="d-flex gap-2">
              <Button
                color="success"
                className="me-2"
                onClick={generateSupplierReport}
                disabled={supplierReportLoading}
              >
                {supplierReportLoading ? (
                  <>
                    <Spinner size="sm" className="me-1" /> Génération...
                  </>
                ) : (
                  <>
                    <FaFileAlt className="me-1" /> Générer
                  </>
                )}
              </Button>
              {supplierReport && (
                <UncontrolledDropdown>
                  <DropdownToggle color="light" caret>
                    <FaColumns className="me-1" /> Colonnes
                  </DropdownToggle>
                  <DropdownMenu end style={{ maxHeight: 360, overflowY: 'auto', minWidth: 220 }}>
                    {reportColumns.filter(column => !column.computed).map(column => (
                      <DropdownItem
                        key={column.key}
                        toggle={false}
                        onClick={(event) => {
                          toggleColumn(column.key);
                        }}
                      >
                        <Input
                          type="checkbox"
                          className="me-2"
                          checked={!!visibleColumns[column.key]}
                          onClick={(event) => event.stopPropagation()}
                          onChange={(event) => {
                            event.stopPropagation();
                            toggleColumn(column.key);
                          }}
                        />
                        {column.label}
                      </DropdownItem>
                    ))}
                  </DropdownMenu>
                </UncontrolledDropdown>
              )}
              {supplierReport && (
                <UncontrolledDropdown>
                  <DropdownToggle color="secondary" caret>
                    <FaDownload className="me-1" /> Export PDF
                  </DropdownToggle>
                  <DropdownMenu>
                    <DropdownItem onClick={() => exportSupplierReportToPDF(true)}>
                      Détails AVEC prix
                    </DropdownItem>
                    <DropdownItem onClick={() => exportSupplierReportToPDF(false)}>
                      Détails SANS prix
                    </DropdownItem>
                    <DropdownItem divider />
                    <DropdownItem onClick={() => exportSupplierRecapToPDF(true)}>
                      Récap AVEC prix
                    </DropdownItem>
                    <DropdownItem onClick={() => exportSupplierRecapToPDF(false)}>
                      Récap SANS prix
                    </DropdownItem>
                  </DropdownMenu>
                </UncontrolledDropdown>
              )}
            </div>
          </div>
        </CardHeader>
        <CardBody>
          <Row className="mb-4">
            <Col lg={3} md={3} sm={6} xs={12}>
              <FormGroup className="mb-0">
                <Label className="mb-1">{t('reglement.search')}</Label>
                <div className="input-group">
                  <span className="input-group-text bg-light">
                    <FaSearch />
                  </span>
                  <Input
                    type="text"
                    placeholder="N°BR, Article, Fournisseur..."
                    value={filters.query}
                    onChange={e => {
                      const value = e.target.value.toUpperCase();
                      setFilters(prevFilters => ({
                        ...prevFilters,
                        query: value
                      }));
                    }}
                    className="text-uppercase"
                    style={{ textTransform: 'uppercase' }}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        debouncedSearch(e.target.value.toUpperCase());
                      }
                    }}
                  />
                </div>
              </FormGroup>
            </Col>
            <Col lg={2} md={2} sm={6} xs={12}>
              <FormGroup className="mb-0">
                <Label className="mb-1">N° BR</Label>
                <Input
                  type="text"
                  placeholder="Rechercher BR"
                  value={filters.brNumber}
                  onChange={e => setFilters(prevFilters => ({ ...prevFilters, brNumber: e.target.value.toUpperCase() }))}
                  className="text-uppercase"
                  style={{ textTransform: 'uppercase' }}
                />
              </FormGroup>
            </Col>
            <Col lg={2} md={2} sm={6} xs={12}>
              <FormGroup className="mb-0">
                <Label className="mb-1">Désignation article</Label>
                <Input
                  type="text"
                  placeholder="Article"
                  value={filters.articleDesignation}
                  onChange={e => setFilters(prevFilters => ({ ...prevFilters, articleDesignation: e.target.value }))}
                />
              </FormGroup>
            </Col>
            <Col lg={2} md={2} sm={6} xs={12}>
              <FormGroup check className="mt-4">
                <Input
                  id="packDallaAchat"
                  type="checkbox"
                  checked={!!filters.packDalla}
                  onChange={e => setFilters(prevFilters => ({ ...prevFilters, packDalla: e.target.checked }))}
                />
                <Label check htmlFor="packDallaAchat">Pack Dalla</Label>
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label for="supplierSelect" className="form-label">
                  <FaUser className="me-1" /> Fournisseur
                </Label>
                <Select
                  id="supplierSelect"
                  options={fournisseurOptionsWithAll}
                  isLoading={suppliersLoading}
                  value={fournisseurOptionsWithAll.find(option => option.value === filters.supplierId)}
                  onChange={(selectedOption) => {
                    setFilters(f => ({ ...f, supplierId: selectedOption ? selectedOption.value : null }));
                  }}
                  placeholder="Sélectionner..."
                  isClearable
                  className="react-select-container"
                  classNamePrefix="react-select"
                  menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                  styles={{
                    menuPortal: base => ({ ...base, zIndex: 9999 })
                  }}
                />
              </FormGroup>
            </Col>
            <Col md="2">
              <Label htmlFor="transporteurId">{t('bonLivraison.transporteur')}:</Label>
              <Select
                id="transporteurId"
                options={transporteurOptionsWithAll}
                value={transporteurOptionsWithAll.find(option => option.value === filters.transporteurId)}
                onChange={(selectedOption) => setFilters(f => ({ ...f, transporteurId: selectedOption ? selectedOption.value : null }))}
                placeholder={t('bonLivraison.selectTransporteur')}
                isSearchable
              />

            </Col>
            <Col md="2">
              <Label htmlFor="entrepotId">{t('bonLivraison.entrepot', 'Entrepot')}:</Label>
              <Select
                id="entrepotId"
                options={entrepotOptionsWithAll}
                value={entrepotOptionsWithAll.find(option => option.value === filters.entrepotId)}
                onChange={(selectedOption) => setFilters(f => ({ ...f, entrepotId: selectedOption ? selectedOption.value : null }))}
                placeholder={t('bonLivraison.selectEntrepot', 'Sélectionner...')}
                isSearchable
                isClearable
                menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                styles={{
                  menuPortal: base => ({ ...base, zIndex: 9999 })
                }}
              />

            </Col>
            <Col md={2}>
              <FormGroup className="mb-0">
                <Label htmlFor="utilisateur">Utilisateur</Label>
                <Select
                  id="utilisateur"
                  options={[{ value: null, label: t('common.all', 'Tous') }, ...utilisateurOptions]}
                  value={[{ value: null, label: t('common.all', 'Tous') }, ...utilisateurOptions].find(option => option.value === filters.utilisateur) || null}
                  onChange={(selectedOption) => setFilters(f => ({ ...f, utilisateur: selectedOption ? selectedOption.value : null }))}
                  placeholder="Tous"
                  isSearchable
                  isClearable
                  menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                  styles={{
                    menuPortal: base => ({ ...base, zIndex: 9999 })
                  }}
                />
              </FormGroup>
            </Col>
            <Col md={2}>
              <FormGroup>
                <Label for="startDate" className="form-label">
                  <FaCalendarAlt className="me-1" /> Date de début
                </Label>
                <Input
                  type="date"
                  id="dateDebut"
                  value={filters.dateDebut}
                  onChange={e => setFilters(f => ({ ...f, dateDebut: e.target.value }))}
                />
              </FormGroup>
            </Col>
            <Col md={2}>
              <FormGroup>
                <Label for="endDate" className="form-label">
                  <FaCalendarAlt className="me-1" /> Date de fin
                </Label>
                <Input
                  type="date"
                  id="dateFin"
                  value={filters.dateFin}
                  onChange={e => setFilters(f => ({ ...f, dateFin: e.target.value }))}
                />
              </FormGroup>
            </Col>

          </Row>

          <div ref={supplierReportRef}>
            {supplierReport ? (
              <div className="client-report p-4 border rounded-4 bg-white shadow-sm animate__animated animate__fadeIn">
                <div className="report-body mb-4">
                  <div className="table-responsive rounded-3 overflow-hidden">
                   
                    <Table bordered hover size="sm" className="mb-0 align-middle">
                      <thead className="table-primary">
                        <tr>
                          {detailTableColumns.map(column => (
                            <th key={column.key} className={column.className?.includes('text-end') ? 'text-end' : 'text-center'}>
                              {column.label}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {supplierReport.bonsReception.map((note, index) => (
                          <tr key={note.id} className={index % 2 === 0 ? "bg-light" : ""}>
                            {detailTableColumns.map(column => (
                              <td key={column.key} className={column.className}>
                                {column.value(note)}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="table-light">
                        <tr>
                          {detailTableColumns.map((column, index) => {
                            const quantityTotal = supplierReport.bonsReception.reduce((acc, note) => acc + (Number(note.quantity) || 0), 0);
                            const amountTotal = supplierReport.bonsReception.reduce((acc, note) => acc + (Number(note.lineTotal) || 0), 0);
                            if (column.key === 'quantity') {
                              return <td key={column.key} className="text-center fw-bold text-primary">{formatNumber(quantityTotal)}</td>;
                            }
                            if (column.key === 'lineTotal') {
                              return <td key={column.key} className="text-end fw-bold text-primary">{formatPrice(amountTotal, false)}</td>;
                            }
                            if (index === 0) {
                              return <td key={column.key} className="text-end fw-bold">Total:</td>;
                            }
                            return <td key={column.key}></td>;
                          })}
                        </tr>
                      </tfoot>
                    </Table>

                  </div>
                </div>

                {supplierReport && supplierReport.summary && <div className="report-summary mb-4">
                  <Row className="g-4">
                    <Col md={6}>
                      <div className="mb-3 bg-light p-3 rounded-3 border border-1 border-secondary">
                        <h6 className="border-bottom pb-2 mb-3 text-secondary fw-bold">Résumé</h6>
                        <Table borderless size="sm" className="summary-table">
                          <tbody>
                            <tr>
                              <td className="fw-bold">Montant Total Consommé</td>
                              <td className="text-end">{formatPrice(supplierReport.summary.totalConsome || 0, false)}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Solde Avant le {format(parseDate(supplierReport.period.from), 'dd/MM/yyyy', { locale: fr })}</td>
                              <td className="text-end">{formatPrice(supplierReport.summary.soldeAvant || 0, false)}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Avance</td>
                              <td className="text-end">{formatPrice(supplierReport.summary.avance || 0, false)}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Réglements normaux (hors avances)</td>
                              <td className="text-end">{formatPrice(supplierReport.summary.reglements || 0, false)}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Avoir</td>
                              <td className="text-end">{formatPrice(supplierReport.summary.avoir || 0, false)}</td>
                            </tr>
                            <tr className="table-light">
                              <td className="fw-bold">Net A Payer</td>
                              {
                                supplierReport.summary.netApaye < 0 ? (
                                  <td className="text-end fw-bold text-danger">{formatPrice(supplierReport.summary.netApaye, false)}</td>
                                ) : (
                                  <td className="text-end fw-bold text-success">{formatPrice(supplierReport.summary.netApaye, false)}</td>
                                )
                              }
                            </tr>
                          </tbody>
                        </Table>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="amount-in-words p-4 bg-white rounded-3 border border-1 border-primary">
                        <p className="mb-1 fw-bold text-primary">ArrÃªté le présent document Ã  la somme de:</p>
                        {
                          supplierReport.summary.netApaye < 0 ?
                            < p className="fst-italic text-danger fs-5">
                              {`Moins ${convertNumberToWords(Math.abs(supplierReport.summary.netApaye))} `}
                              Dirhams
                            </p> :
                            <p className="fst-italic text-success fs-5">
                              {`${convertNumberToWords(Math.abs(supplierReport.summary.netApaye))} `}
                              Dirhams
                            </p>
                        }
                        <p className="mt-3 small text-muted">
                          <FaCalendarAlt className="me-1" />
                          NB: Montant Total Consommé sur la période du <span className="fw-semibold">{format(parseDate(supplierReport.period.from), 'dd/MM/yyyy', { locale: fr })} au {format(parseDate(supplierReport.period.to), 'dd/MM/yyyy', { locale: fr })}</span>
                        </p>


                      </div>
                    </Col>
                  </Row>
                </div>
                }
                <div className="report-footer mt-4 pt-3 border-top text-muted small">
                  <div className="d-flex justify-content-between align-items-center">
                    <span><FaCalendarAlt className="me-1" /> Document généré le: <span className="fw-semibold">{supplierReport.generationDate}</span></span>
                    <span className="fw-bold">Page 1</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center p-5 bg-light rounded-4 border border-2 border-secondary animate__animated animate__fadeIn">
                {supplierReportLoading ? (
                  <div>
                    <Spinner color="primary" />
                    <p className="mt-3">Génération du rapport en cours...</p>
                  </div>
                ) : (
                  <div>
                    <FaFileAlt size={48} className="text-muted mb-3" />
                    <h5 className="fw-bold">Aucun rapport généré</h5>
                    <p className="text-muted">
                      Veuillez sélectionner un fournisseur et cliquer sur <span className="fw-semibold">"Générer le rapport"</span> pour afficher les détails des ventes.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </CardBody>
      </Card>

      <style jsx>{`
        .client-report {
          font-size: 0.9rem;
        }
        .summary-table td {
          padding: 0.4rem 0.75rem;
        }
        .company-info h5 {
          color: #2c3e50;
        }
        .amount-in-words {
          border-left: 3px solid #3498db;
        }
        @media print {
          .page-content {
            padding: 0 !important;
          }
          .client-report {
            border: none !important;
          }
        }
      `}</style>
    </Container>
  );
};

export default AchatsStatistiques;
